import React, { memo, useRef, useState } from 'react'

import { useHandler } from '@surglogs-app/hooks'

import { FileTypeGroup } from '../constants'
import {
  HandleFileUploadChangeType,
  HandleFileUploadChangeValue,
  useFileUploadHandler,
} from '../hook'
import { useFileSizeHandler } from '../hook/useFileSizeHandler'
import { getUploadPercentage } from '../services'
import { UploadResponse } from '../types'

import { FileInput } from './FileInput'

export interface FileInputFieldProps {
  fileTypesGroup?: FileTypeGroup
  value: Partial<HandleFileUploadChangeValue>
  onChange: (value: Partial<HandleFileUploadChangeValue> | undefined) => void
  upload: (
    file: File,
    fileUploadProgress: (e: ProgressEvent | number) => void,
  ) => Promise<UploadResponse>
  showFailureAlert?: boolean
  onRemoveClick?: () => void
}

export const FileInputField = memo(
  ({
    value,
    fileTypesGroup = FileTypeGroup.ALL,
    onChange,
    upload,
    showFailureAlert,
    onRemoveClick: onRemoveClickProp,
    ...rest
  }: FileInputFieldProps) => {
    const fileIdsRef = useRef<number[]>([])
    const [uploadProgress, setUploadProgress] = useState(0)

    const handleChange = useHandler<HandleFileUploadChangeType>((input) => {
      onChange(input && !(input instanceof Error) ? input : undefined)
    })

    const onUploadProgress = (e: ProgressEvent | number) =>
      setUploadProgress(getUploadPercentage(e))

    const handleUpload = async (file: File) => {
      const v = await upload(file, onUploadProgress)
      onUploadProgress(0)
      return v
    }

    const handleFileUpload = useFileUploadHandler({
      fileIdsRef,
      fileTypesGroup,
      upload: handleUpload,
      handleChange,
      showFailureAlert,
    })

    const changeHandler = useFileSizeHandler({
      isSingleFileUpload: true,
      onValidFile: handleFileUpload,
    })

    const onRemoveClick = useHandler(() => {
      handleChange(undefined)
      fileIdsRef.current = []
    })

    return (
      <FileInput
        {...rest}
        {...{
          multiple: false,
          onChange: changeHandler,
          uploadProgress,
          onRemoveClick: onRemoveClickProp ?? onRemoveClick,
          fileName: value?.fileName,
          thumbnail: value?.thumbnail,
          url: value?.url,
          fileTypesGroup,
        }}
      />
    )
  },
)
